<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {ElRadioButton, ElRadioGroup, ElTable, ElTableColumn} from 'element-plus';
import {DashboardReeksEnum, formatFte, formatPercentage, ImpactPerOrganisatie, KLEUR_CEL, MaxFormatieTrend, PERSONEEL_VARIANTEN, PersoneelPage, PersoneelVariant, PersoneelVariant_name, StatusKleur, statusTekst} from 'common';
import {dashboardPersoneel} from '@/base/callBackend.js';
import {dashboardOrgs, dashboardPeriod, laadDashboard} from '@/base/dashboardState.js';
import {localStorageGetJson, localStorageSetJson} from '@/base/utils.js';
import StatusMatrix from '@/views/dashboard/StatusMatrix.vue';
import ImpactPerOrgChart from '@/views/dashboard/ImpactPerOrgChart.vue';
import ReeksChart from '@/views/dashboard/ReeksChart.vue';
import ReeksNotities from '@/views/dashboard/ReeksNotities.vue';
import KpiCard from '@/views/dashboard/KpiCard.vue';

const STORAGE_VARIANT_ID: string  = 'dashboard:personeel:variant';
const STORAGE_WEERGAVE_ID: string = 'dashboard:personeel:weergave';
const UITLEG_TREND: string        = 'FTE-ontwikkeling sinds de eerste meting: eigen personeel op loonlijst, PNIL en artsen in MSB. N.B. SVRZ telt ook mee in het volume van ZiB.';

type Weergave = 'grafiek' | 'tabel';

const loading  = ref<boolean>(true);
const page     = ref<PersoneelPage | undefined>(undefined);
const variant  = ref<PersoneelVariant>(PERSONEEL_VARIANTEN.includes(localStorageGetJson(STORAGE_VARIANT_ID)) ? localStorageGetJson(STORAGE_VARIANT_ID) : 'samenvatting');
const weergave = ref<Weergave>(localStorageGetJson(STORAGE_WEERGAVE_ID) === 'tabel' ? 'tabel' : 'grafiek');
let loadSeq: number = 0;

const eenTrend = computed<MaxFormatieTrend | undefined>(() => page.value !== undefined && page.value.trends.length === 1 ? page.value.trends[0] : undefined);
const tabelRijen = computed<ImpactPerOrganisatie[]>(() => page.value === undefined || page.value.impact.length === 0 ? [] : [...page.value.impact, page.value.impactTotaal]);

watch(variant, (v: PersoneelVariant) => localStorageSetJson(STORAGE_VARIANT_ID, v));
watch(weergave, (w: Weergave) => localStorageSetJson(STORAGE_WEERGAVE_ID, w));
watch([dashboardPeriod, dashboardOrgs, variant], load, {immediate: true});

async function load() {
    if (dashboardPeriod.value === '') {
        return;
    }
    const seq: number                       = ++loadSeq;
    loading.value                           = true;
    const result: PersoneelPage | undefined = await laadDashboard(() => dashboardPersoneel(dashboardPeriod.value, dashboardOrgs.value, variant.value));
    if (seq !== loadSeq) {
        return;
    }
    page.value    = result;
    loading.value = false;
}

function fte(v: number | undefined): string {
    return v === undefined ? 'n.v.t.' : formatFte(v);
}

function rowClassName({row}: {row: ImpactPerOrganisatie}): string {
    return row.organisatie === 'Totaal' ? 'font-semibold' : '';
}

function statusBg(kleur: StatusKleur): string {
    return KLEUR_CEL[kleur];
}
</script>

<template>
    <div class="px-10 py-4 flex flex-col gap-8">
        <div v-if="loading" class="text-sm text-gray-500">laden...</div>
        <template v-else-if="page">
            <section class="flex flex-col gap-2">
                <div class="flex flex-row items-center gap-4">
                    <div class="text-xl font-semibold text-zzc-blue">Targetmatrix</div>
                    <el-radio-group v-model="variant">
                        <el-radio-button v-for="v in PERSONEEL_VARIANTEN" :key="v" :value="v">{{ PersoneelVariant_name(v) }}</el-radio-button>
                    </el-radio-group>
                </div>
                <StatusMatrix :matrix="page.matrix" :kop-label="variant === 'samenvatting' ? undefined : 'Organisatie'"/>
            </section>

            <section class="flex flex-col gap-2">
                <div class="flex flex-row items-center gap-4">
                    <div class="text-xl font-semibold text-zzc-blue">FTE-impact per organisatie</div>
                    <el-radio-group v-model="weergave">
                        <el-radio-button value="grafiek">Grafiek</el-radio-button>
                        <el-radio-button value="tabel">Tabel</el-radio-button>
                    </el-radio-group>
                </div>
                <ImpactPerOrgChart v-if="weergave === 'grafiek'" :impact="page.impact"/>
                <el-table v-else :data="tabelRijen" size="small" :row-class-name="rowClassName">
                    <el-table-column prop="organisatie" label="Organisatie" width="160"/>
                    <el-table-column label="Geidentificeerd" width="220">
                        <template #default="scope">{{ fte(scope.row.welInUitvoering + scope.row.nietInUitvoering) }} <span class="text-gray-500">(wel {{ fte(scope.row.welInUitvoering) }}, niet {{ fte(scope.row.nietInUitvoering) }})</span></template>
                    </el-table-column>
                    <el-table-column label="Gemeten" width="110"><template #default="scope">{{ fte(scope.row.gemeten) }}</template></el-table-column>
                    <el-table-column label="Target Q" width="110"><template #default="scope">{{ fte(scope.row.targetQ) }}</template></el-table-column>
                    <el-table-column label="Target Q+1" width="110"><template #default="scope">{{ fte(scope.row.targetQ1) }}</template></el-table-column>
                    <el-table-column label="Voortgang" width="100"><template #default="scope">{{ formatPercentage(scope.row.voortgang) }}</template></el-table-column>
                    <el-table-column label="Status" width="180">
                        <template #default="scope"><span class="px-2 py-0.5 rounded" :style="{backgroundColor: statusBg(scope.row.kleur)}">{{ statusTekst(scope.row.kleur) }}</span></template>
                    </el-table-column>
                </el-table>
            </section>

            <section class="flex flex-col gap-2">
                <div class="text-xl font-semibold text-zzc-blue">Maximale formatie</div>
                <div class="text-sm text-gray-500">{{ UITLEG_TREND }}</div>
                <div v-if="page.trends.length === 0" class="text-sm text-gray-500">Geen gegevens voor deze selectie.</div>
                <div v-else-if="eenTrend" class="flex flex-row gap-6">
                    <div class="flex-1"><ReeksChart :serie="eenTrend.serie" type="line"/></div>
                    <div class="flex flex-col gap-2">
                        <KpiCard label="Eerste meting" :value="eenTrend.eersteMeting ? `${eenTrend.eersteMeting.kwartaal}: ${formatFte(eenTrend.eersteMeting.waarde)}` : 'n.v.t.'"/>
                        <KpiCard label="Target Q+1" :value="fte(eenTrend.targetQ1)"/>
                        <KpiCard label="Status laatste punt" :value="statusTekst(eenTrend.kleur)" :accent="KLEUR_CEL[eenTrend.kleur]"/>
                    </div>
                </div>
                <div v-else class="grid grid-cols-3 gap-4">
                    <div v-for="t in page.trends" :key="t.organisatie" class="rounded border border-gray-200 bg-white p-2">
                        <div class="text-sm font-semibold">{{ t.organisatie }} <span class="font-normal text-gray-500">- {{ statusTekst(t.kleur) }}</span></div>
                        <ReeksChart :serie="t.serie" type="line" compact/>
                    </div>
                </div>
                <ReeksNotities v-if="eenTrend && eenTrend.notities.length > 0" :notities="eenTrend.notities" :reeksen="[DashboardReeksEnum.maxFormatie]"/>
            </section>
        </template>
        <div v-else class="text-sm text-gray-500">Geen gegevens (laden mislukt).</div>
    </div>
</template>
